import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays, Plus } from "lucide-react";
import { api, formatApiError, todayStr, brl } from "@/lib/api";
import { ContextMenu } from "@/components/ContextMenu";
import { SaleDialog } from "@/components/admin/SaleDialog";

const SLOTS = (() => {
  const out = [];
  for (let m = 9 * 60; m <= 19 * 60; m += 30) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
  }
  return out;
})();

const fmtDateLong = (d) => new Date(d + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });

const shiftDate = (dateStr, days) => {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function Agenda() {
  const [date, setDate] = useState(todayStr());
  const [appointments, setAppointments] = useState([]);
  const [salesById, setSalesById] = useState({});
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [menu, setMenu] = useState(null);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [editingAppt, setEditingAppt] = useState(null);
  const [saleCtx, setSaleCtx] = useState(null); // {client, appointment}
  const [selected, setSelected] = useState(null);

  const fetchDay = () => {
    api.get("/appointments", { params: { date } }).then((r) => setAppointments(r.data)).catch(() => {});
    api.get("/sales", { params: { start: date, end: date } }).then((r) => {
      const map = {};
      r.data.forEach((s) => (map[s.id] = s));
      setSalesById(map);
    }).catch(() => {});
  };

  useEffect(() => {
    fetchDay();
  }, [date]);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const byTime = useMemo(() => {
    const map = {};
    appointments.forEach((a) => (map[a.time] = a));
    return map;
  }, [appointments]);

  const openReserve = (time) => {
    setEditingAppt(null);
    setReserveOpen({ time });
  };

  const deleteAppt = async (appt) => {
    const msg = appt.sale_id
      ? `"${appt.client_name}" às ${appt.time} já tem venda registrada. O agendamento será excluído, mas a venda e o pagamento permanecem nos relatórios. Continuar?`
      : `Excluir o agendamento de ${appt.client_name} às ${appt.time}?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/appointments/${appt.id}`);
      toast.success("Agendamento excluído");
      setSelected(null);
      fetchDay();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteApptRef = useRef(deleteAppt);
  deleteApptRef.current = deleteAppt;

  // Tecla Delete exclui o agendamento selecionado
  useEffect(() => {
    const h = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const tag = document.activeElement?.tagName;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) return;
      const appt = appointments.find((a) => a.id === selected);
      if (!appt) return;
      e.preventDefault();
      deleteApptRef.current(appt);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [selected, appointments]);

  useEffect(() => {
    setSelected(null);
  }, [date]);

  return (
    <div data-testid="agenda-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6 no-print">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-light">Agenda</h1>
          <p className="text-sm text-stone-500 capitalize mt-1" data-testid="agenda-current-date">{fmtDateLong(date)}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setDate(shiftDate(date, -1))} className="w-10 h-10 rounded-full border border-stone-300 flex items-center justify-center hover:bg-white transition-colors" data-testid="agenda-prev-day-button" aria-label="Dia anterior">
            <ChevronLeft size={17} />
          </button>
          <div className="relative">
            <CalendarDays size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C8A279] pointer-events-none" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value || todayStr())}
              data-testid="agenda-date-picker"
              className="rounded-full border border-stone-300 bg-white pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#C8A279]"
            />
          </div>
          <button onClick={() => setDate(shiftDate(date, 1))} className="w-10 h-10 rounded-full border border-stone-300 flex items-center justify-center hover:bg-white transition-colors" data-testid="agenda-next-day-button" aria-label="Próximo dia">
            <ChevronRight size={17} />
          </button>
          <button onClick={() => setDate(todayStr())} className="text-sm px-4 py-2.5 rounded-full text-stone-600 hover:bg-white transition-colors" data-testid="agenda-today-button">
            Hoje
          </button>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-4 sm:p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
        <p className="text-xs text-stone-400 mb-3 hidden sm:flex items-center gap-5 flex-wrap">
          <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#FAF9F5] border border-stone-300 inline-block" /> Livre</span>
          <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#FFFBEB] border border-amber-300 inline-block" /> Agendado</span>
          <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#F0FDF4] border border-green-300 inline-block" /> Pago / Concluído</span>
          <span className="text-stone-400">· Clique num agendado para selecionar e aperte <kbd className="px-1.5 py-0.5 bg-stone-100 border border-stone-300 rounded text-[10px] font-semibold">Delete</kbd> para excluir — ou botão direito → Excluir</span>
        </p>
        <div className="grid grid-cols-1 gap-2" data-testid="agenda-slots-grid">
          {SLOTS.map((time) => {
            const appt = byTime[time];
            const sale = appt?.sale_id ? salesById[appt.sale_id] : null;
            const pago = sale && sale.status === "paga";
            const fiadoPendente = sale && sale.status === "fiado" && !sale.paid;
            const atendido = appt && (appt.status === "concluido" || appt.sale_id);
            return (
              <div
                key={time}
                data-testid={`agenda-time-slot-${time.replace(":", "")}`}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenu({
                    x: e.clientX,
                    y: e.clientY,
                    appt,
                    time,
                  });
                }}
                onClick={() => (appt ? setSelected(selected === appt.id ? null : appt.id) : openReserve(time))}
                className={`flex items-center gap-4 rounded-xl px-4 py-3 border transition-colors cursor-pointer ${
                  selected === appt?.id
                    ? "ring-2 ring-[#C8A279]"
                    : ""
                } ${
                  appt
                    ? atendido
                      ? "bg-[#F0FDF4] border-green-200 hover:border-green-400"
                      : "bg-[#FFFBEB] border-amber-200 hover:border-amber-400"
                    : "bg-[#FAF9F5] border-stone-100 hover:border-stone-300"
                }`}
              >
                <span className={`text-sm font-semibold tabular-nums w-14 ${atendido ? "text-green-700" : appt ? "text-amber-700" : "text-stone-400"}`}>{time}</span>
                {appt ? (
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate flex items-center gap-2" data-testid={`agenda-slot-client-${time.replace(":", "")}`}>
                      {appt.client_name}
                      {appt.phone ? <span className="text-stone-400 font-normal"> · {appt.phone}</span> : null}
                      {pago && (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-green-600 text-white rounded-full px-2 py-0.5" data-testid={`agenda-slot-pago-${time.replace(":", "")}`}>
                          ✓ Pago
                        </span>
                      )}
                      {fiadoPendente && (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-amber-500 text-white rounded-full px-2 py-0.5" data-testid={`agenda-slot-fiado-${time.replace(":", "")}`}>
                          Fiado
                        </span>
                      )}
                      {atendido && !pago && !fiadoPendente && (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-stone-500 text-white rounded-full px-2 py-0.5">
                          ✓ Atendido
                        </span>
                      )}
                    </p>
                    {appt.service ? <p className="text-xs text-stone-500 truncate">{appt.service}{appt.notes ? ` · ${appt.notes}` : ""}</p> : appt.notes ? <p className="text-xs text-stone-500 truncate">{appt.notes}</p> : null}
                  </div>
                ) : (
                  <span className="text-sm text-stone-300 flex-1 flex items-center gap-2">
                    <Plus size={13} /> Livre — clique para reservar
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          items={
            menu.appt
              ? [
                  { label: "Abrir Venda", testId: "abrir-venda", action: () => setSaleCtx({ appointment: menu.appt, saleId: menu.appt.sale_id || null }) },
                  { label: "Modificar", testId: "modificar", action: () => { setReserveOpen({ time: menu.appt.time }); setEditingAppt(menu.appt); } },
                  { label: "Excluir", testId: "excluir", danger: true, action: () => deleteAppt(menu.appt) },
                ]
              : [{ label: "Reservar Horário", testId: "reservar", action: () => openReserve(menu.time) }]
          }
        />
      )}

      {reserveOpen && (
        <ReserveModal
          date={date}
          time={reserveOpen.time}
          appointment={editingAppt}
          clients={clients}
          services={services}
          onClientCreated={(c) => setClients([...clients, c])}
          onClose={() => { setReserveOpen(false); setEditingAppt(null); }}
          onSaved={(appt, openSale) => { setReserveOpen(false); setEditingAppt(null); fetchDay(); if (openSale && appt) setSaleCtx({ appointment: appt }); }}
        />
      )}

      {saleCtx && (
        <SaleDialog
          saleId={saleCtx.saleId}
          appointment={saleCtx.appointment}
          services={services}
          onClose={() => setSaleCtx(null)}
          onSaved={() => { setSaleCtx(null); fetchDay(); }}
        />
      )}
    </div>
  );
}

function ReserveModal({ date, time, appointment, clients, services, onClientCreated, onClose, onSaved }) {
  const isEdit = Boolean(appointment);
  const [form, setForm] = useState({
    time: appointment?.time || time,
    client_name: appointment?.client_name || "",
    phone: appointment?.phone || "",
    service: appointment?.service || "",
    service_id: appointment?.service_id || "",
    notes: appointment?.notes || "",
  });
  const [clientId, setClientId] = useState(appointment?.client_id || null);
  const [search, setSearch] = useState(appointment?.client_name || "");
  const [newClient, setNewClient] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hl, setHl] = useState(-1);
  const dropdownRef = useRef(null);
  const clientBoxRef = useRef(null);
  const [searchSvc, setSearchSvc] = useState(appointment?.service || "");
  const [hlSvc, setHlSvc] = useState(-1);
  const svcRef = useRef(null);
  const svcBoxRef = useRef(null);
  const [clientOpen, setClientOpen] = useState(false);
  const [svcFocus, setSvcFocus] = useState(false);

  const matches = clients
    .filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || (c.phone || "").includes(search.trim()))
    .slice(0, 8);

  const matchesSvc = services
    .filter((s) => s.name.toLowerCase().includes(searchSvc.trim().toLowerCase()))
    .slice(0, 8);
  const svcDisplay = matchesSvc.length ? matchesSvc : services.slice(0, 8);
  const svcOpen = svcFocus && services.length > 0;

  useEffect(() => {
    if (!clientOpen && !svcFocus) return;
    const h = (e) => {
      if (clientBoxRef.current?.contains(e.target) || svcBoxRef.current?.contains(e.target)) return;
      setClientOpen(false);
      setSvcFocus(false);
    };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, [clientOpen, svcFocus]);

  useEffect(() => {
    setHl(-1);
  }, [search]);

  useEffect(() => {
    const el = dropdownRef.current?.querySelector(`[data-hl="${hl}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [hl]);

  const pickClient = (c) => {
    setClientId(c.id);
    setSearch(c.name);
    setForm({ ...form, client_name: c.name, phone: c.phone || "" });
    setNewClient(false);
    setHl(-1);
    setClientOpen(false);
  };

  const onSearchKeyDown = (e) => {
    const total = matches.length;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (total) setHl((h) => (h + 1) % total);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (total) setHl((h) => (h <= 0 ? total - 1 : h - 1));
    } else if (e.key === "Enter") {
      if (hl >= 0 && hl < total) {
        e.preventDefault();
        pickClient(matches[hl]);
      }
    } else if (e.key === "Escape") {
      setHl(-1);
      setClientOpen(false);
    }
  };

  const pickSvc = (s) => {
    setForm({ ...form, service: s.name, service_id: s.id });
    setSearchSvc(s.name);
    setHlSvc(-1);
    setSvcFocus(false);
  };

  const onSvcKeyDown = (e) => {
    const total = svcDisplay.length;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (total) setHlSvc((h) => (h + 1) % total);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (total) setHlSvc((h) => (h <= 0 ? total - 1 : h - 1));
    } else if (e.key === "Enter") {
      if (hlSvc >= 0 && hlSvc < total) {
        e.preventDefault();
        pickSvc(svcDisplay[hlSvc]);
      }
    } else if (e.key === "Escape") {
      setHlSvc(-1);
      setSvcFocus(false);
    }
  };

  useEffect(() => {
    const el = svcRef.current?.querySelector(`[data-hl="${hlSvc}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [hlSvc]);

  const submit = async (thenSale) => {
    if (!form.client_name.trim()) {
      toast.error("Informe o cliente");
      return;
    }
    setBusy(true);
    const payload = { date, time: form.time, client_id: clientId, client_name: form.client_name.trim(), phone: form.phone, service: form.service, service_id: form.service_id || null, notes: form.notes, status: "agendado" };
    try {
      const res = isEdit ? await api.put(`/appointments/${appointment.id}`, payload) : await api.post("/appointments", payload);
      toast.success(isEdit ? "Agendamento atualizado" : "Horário reservado!");
      onSaved(res.data, thenSale);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const createClient = async () => {
    if (!search.trim()) return;
    try {
      const { data } = await api.post("/clients", { name: search.trim(), phone: form.phone, notes: "" });
      onClientCreated(data);
      pickClient(data);
      setNewClient(false);
      toast.success("Cliente cadastrado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const field = "w-full rounded-lg border border-stone-300 px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C8A279] focus:ring-2 focus:ring-[#C8A279]/20 transition-all";

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4 bg-black/50" data-testid="reserve-modal">
      <div className="bg-[#FAF9F5] rounded-2xl w-full max-w-md shadow-2xl max-h-[92vh] overflow-y-auto thin-scroll">
        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C8A279]">{isEdit ? "Modificar" : "Reservar"}</p>
            <p className="font-display text-xl capitalize">{fmtDateLong(date)} · {form.time}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-200 transition-colors" data-testid="reserve-close-button" aria-label="Fechar">×</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Horário</label>
            <select value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={`mt-1.5 ${field}`} data-testid="reserve-time-select">
              {SLOTS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="relative" ref={clientBoxRef}>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Cliente</label>
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setClientId(null); setForm({ ...form, client_name: e.target.value }); setNewClient(false); setClientOpen(true); }}
              onFocus={() => setClientOpen(true)}
              onClick={() => setClientOpen(true)}
              onKeyDown={onSearchKeyDown}
              placeholder="Digite para buscar… (↓ ↑ para navegar, Enter para escolher)"
              className={`mt-1.5 ${field}`}
              data-testid="reserve-client-search"
              autoComplete="off"
            />
            {clientOpen && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg overflow-hidden" data-testid="reserve-client-dropdown" ref={dropdownRef}>
                {matches.map((c, i) => (
                  <button
                    key={c.id}
                    data-hl={i}
                    onClick={() => pickClient(c)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${i === hl ? "bg-[#F3EFE6] font-medium" : "hover:bg-stone-100"}`}
                    data-testid={`reserve-client-option-${c.id}`}
                  >
                    <span>
                      {c.name} {c.phone ? <span className="text-stone-400">· {c.phone}</span> : null}
                    </span>
                    {clientId === c.id && <span className="text-[10px] font-semibold uppercase tracking-wider text-green-600">Selecionado</span>}
                  </button>
                ))}
                {search.trim() && !matches.some((c) => c.name.toLowerCase() === search.toLowerCase()) && (
                  <button
                    onClick={() => setNewClient(true)}
                    data-hl={matches.length}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors border-t border-stone-100 ${hl === matches.length ? "bg-[#F3EFE6] font-medium text-[#B38F68]" : "text-[#B38F68] hover:bg-stone-50"}`}
                    data-testid="reserve-new-client-option"
                  >
                    + Cadastrar "{search}"
                  </button>
                )}
                {newClient && (
                  <div className="p-3 bg-stone-50 space-y-2 border-t border-stone-100" data-testid="reserve-new-client-form">
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefone" className={field} data-testid="reserve-new-client-phone" />
                    <button onClick={createClient} className="w-full text-sm bg-[#121212] text-white rounded-lg py-2 hover:bg-stone-700 transition-colors" data-testid="reserve-new-client-save">Salvar cliente</button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="relative" ref={svcBoxRef}>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Serviço</label>
            <input
              value={searchSvc}
              onChange={(e) => {
                setSearchSvc(e.target.value);
                setForm({ ...form, service: e.target.value, service_id: "" });
                setSvcFocus(true);
              }}
              onFocus={() => setSvcFocus(true)}
              onClick={() => setSvcFocus(true)}
              onKeyDown={onSvcKeyDown}
              placeholder="Digite para buscar… (↓ ↑ para navegar, Enter para escolher)"
              className={`mt-1.5 ${field}`}
              data-testid="reserve-service-search"
              autoComplete="off"
            />
            {searchSvc && form.service_id && searchSvc === form.service && (
              <button
                onClick={() => { setSearchSvc(""); setForm({ ...form, service: "", service_id: "" }); }}
                className="absolute right-3 top-[38px] text-xs text-stone-400 hover:text-red-500 transition-colors"
                data-testid="reserve-service-clear"
              >
                limpar
              </button>
            )}
            {svcOpen && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg overflow-hidden" data-testid="reserve-service-dropdown" ref={svcRef}>
                {matchesSvc.length === 0 && searchSvc.trim() && services.length > 0 && (
                  <p className="px-4 py-2 text-xs text-stone-400 border-t border-stone-100">
                    Nenhum serviço com "{searchSvc}" — todos do cadastro:
                  </p>
                )}
                {svcDisplay.map((s, i) => (
                  <button
                    key={s.id}
                    data-hl={i}
                    onClick={() => pickSvc(s)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${i === hlSvc ? "bg-[#F3EFE6] font-medium" : "hover:bg-stone-100"}`}
                    data-testid={`reserve-service-option-${s.id}`}
                  >
                    <span>
                      {s.name} {form.service_id === s.id && <span className="text-[10px] font-semibold uppercase tracking-wider text-green-600">· Selecionado</span>}
                    </span>
                    <span className="text-stone-400">{brl(s.price)}</span>
                  </button>
                ))}
                {services.length === 0 && (
                  <p className="px-4 py-3 text-xs text-stone-400 border-t border-stone-100">
                    Nenhum serviço cadastrado — cadastre na aba Serviços.
                  </p>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Observações</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className={`mt-1.5 ${field}`} data-testid="reserve-notes-input" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-stone-200 flex flex-wrap gap-3 justify-end">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-stone-900 transition-colors" data-testid="reserve-cancel-button">Cancelar</button>
          {!isEdit && (
            <button onClick={() => submit(true)} disabled={busy} className="px-5 py-2.5 rounded-full bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50" data-testid="reserve-save-open-sale-button">
              Salvar + Abrir Venda
            </button>
          )}
          <button onClick={() => submit(false)} disabled={busy} className="px-6 py-2.5 rounded-full bg-[#121212] text-white text-sm font-medium hover:bg-stone-700 transition-colors disabled:opacity-50" data-testid="reserve-save-button">
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
